import { extractPackageGroupingKeyFromGroupKey, type GroupedPropertyRate } from 'lib/property/groupPropertyRates'

import type { RateView } from './singlePropertyUtils'

/**
 * Discriminated union describing how each rate row should be rendered in PropertyRoomOption.
 * LPC and legacy bedbank rates pass through unchanged; unified property rates either render
 * as a single card or collapse into a merged variant-selector card.
 */
export type PropertyRateRenderItem =
  | { type: 'lpc', view: RateView & { lpcRate: NonNullable<RateView['lpcRate']> } }
  | { type: 'bedbank', view: RateView & { bedbankRate: NonNullable<RateView['bedbankRate']> } }
  | { type: 'property', view: RateView & { propertyRate: NonNullable<RateView['propertyRate']> } }
  | { type: 'merged', group: GroupedPropertyRate }

/**
 * Converts the flat display-rate list into render items, collapsing grouped Sabre property
 * rates (standard + loyalty-eligible variants) into a single merged card per group.
 *
 * `displayRates` preserves accommodation-service ordering; `groupedPropertyRates` is built
 * separately by `groupPropertyRates` and keyed by board + inclusion hash.
 */
export function buildPropertyRateRenderItems(
  displayRates: Array<RateView>,
  groupedPropertyRates: Array<GroupedPropertyRate>,
): Array<PropertyRateRenderItem> {
  const groupByKey = new Map(groupedPropertyRates.map(group => [group.groupingKey, group]))
  // Each merged group emits one card on first encounter; subsequent variants are skipped.
  const seenGroups = new Set<string>()
  const items: Array<PropertyRateRenderItem> = []

  displayRates.forEach(view => {
    // LPC and legacy bedbank views are not subject to property-rate grouping.
    if (view.lpcRate) {
      items.push({ type: 'lpc', view: view as Extract<PropertyRateRenderItem, { type: 'lpc' }>['view'] })
      return
    }

    if (view.bedbankRate) {
      items.push({ type: 'bedbank', view: view as Extract<PropertyRateRenderItem, { type: 'bedbank' }>['view'] })
      return
    }

    if (!view.propertyRate) {
      return
    }

    const groupingKey = extractPackageGroupingKeyFromGroupKey(view.propertyRate.groupKey)
    const group = groupByKey.get(groupingKey)

    // Multiple variants in the same group → one merged card with a radio selector.
    if (group && group.rates.length > 1) {
      if (seenGroups.has(groupingKey)) {
        return
      }
      seenGroups.add(groupingKey)
      items.push({ type: 'merged', group })
      return
    }

    // Ungrouped or sole variant in a group → render as a standalone property rate card.
    items.push({ type: 'property', view: view as Extract<PropertyRateRenderItem, { type: 'property' }>['view'] })
  })

  return items
}
